import { Button } from "@shopify/polaris";
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import classes from "./Billingplan.module.css";
import shell from "../onboarding/OnboardingShell.module.css";
import OnboardingShell from "../onboarding/OnboardingShell";
import {
  getAllBillingPlan,
  getStoreDetails,
  getAccountDetails,
  getCurrentBillingPlan,
} from "../../services/index";
import {billingPlanHasPurchaseOrderFeature, CHANGE_PLAN_STATUS, PAID_PLANS_STATUS, PRO_PLAN_STATUS} from "../../CONSTANTS/APPCONSTANTS"
import CustomToast from "../UI/CustomToast/CustomToast";
import { initIntercom } from "../../utils/thirdparty/intercom";
import { trackAmplitudeUserShownRevenueLimitExceededPageEvent } from "../../utils/thirdparty/amplitude";
import BillingPlanFeatureList from "./BillingPlanFeatureList";
import { PRIVACY, TERMS, ONBOARDING } from "../../navigation/CONSTANTS";
import { openEmbeddedAppPathInNewAdminTab } from "../../utils/shopifyAdminUrls";

/*
 * Plans shown on the billing cards.
 * On change-plan, hide the shop's current plan unless that would leave the page empty
 * (e.g. a whitelisted shop whose list only contains the discounted plan they already have).
 *
 * Example: [current, paid] + change_plan → [paid]
 * Example: [current] + change_plan → [current]
 */
function getDisplayedBillingPlans(billingPlans, currentPlan, status) {
  const plans = Array.isArray(billingPlans) ? billingPlans.filter(Boolean) : [];
  if (status !== CHANGE_PLAN_STATUS || !currentPlan?.id) {
    return plans;
  }
  const plansExceptCurrent = plans.filter((plan) => plan.id !== currentPlan.id);
  return plansExceptCurrent.length ? plansExceptCurrent : plans;
}

/*
 * CTA label for a plan card. currentPlan can be null while /billingplan/status
 * is still loading or when that API returns data: null — never read .price on it.
 *
 * Example: trial expired, cheaper listed plan, change_plan → "Downgrade Plan"
 * Example: trial expired, otherwise → "Upgrade Plan"
 * Example: cheaper listed plan, change_plan → "Downgrade Plan"
 * Example: change_plan → "Upgrade Plan"
 * Example: otherwise → "Start Free Trial"
 */
function getPlanCtaLabel(billingPlan, currentPlan, isFreeTrialExpired, status) {
  const listedMonthlyPrice = billingPlan?.price?.monthly;
  const currentPrice = currentPlan?.price;
  const isDowngrade =
    listedMonthlyPrice != null &&
    currentPrice != null &&
    listedMonthlyPrice < currentPrice &&
    status === CHANGE_PLAN_STATUS;

  if (isFreeTrialExpired && !billingPlan?.is_free) {
    return isDowngrade ? "Downgrade Plan" : "Upgrade Plan";
  }

  if (isDowngrade) {
    return "Downgrade Plan";
  }

  if (status === CHANGE_PLAN_STATUS) {
    return "Upgrade Plan";
  }

  return "Start Free Trial";
}

const Billingplan = (props) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get("status");
  const [isExceedStatus, setIsExceedStatus] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [isSinglePlanDisplay, setSinglePlanDisplay] = useState(false);
  const [isStoreDetails, setIsStoreDetails] = useState("");
  const [isFreeTrialExpired, setIsFreeTrialExpired] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSelectedButtonIndex, setIsSelectedButtonIndex] = useState(-1);
  const [billingPlans, setBillingPlans] = useState([]);
  function onSelectBillingPlan(index, billingPlan) {
    setIsSelectedButtonIndex(index);
    let isSubcriptionType = !isMonthlyButtonActive;
    let selectedPlan = {
      plan_id: billingPlan.id,
      annual: isSubcriptionType.toString(),
      next: "test",
    };

    setIsSubmitted(true);
    props.selectBillingPlan(selectedPlan);
  }

  //For the second phase of the billing plan, comment code features will be enabled.

  // const [isToggleIndex, setIsToggleIndex] = useState();

  // const [showPlanFeatures, setIsShowPlanFeatures] = useState(false);

  // const hideShowPlanFeatures = (index) => {
  //   console.log(index);

  //   if (isToggleIndex === index) {
  //     setIsToggleIndex(null);
  //   } else {
  //     setIsToggleIndex(index);
  //   }

  //   setIsShowPlanFeatures(!showPlanFeatures);
  // };

  const [isMonthlyButtonActive, setIsMonthlyButtonActive] = useState(true);

  // const handleMonthlyButtonClick = useCallback(() => {
  //   if (isMonthlyButtonActive) return;

  //   setIsMonthlyButtonActive(true);
  // }, [isMonthlyButtonActive]);

  // const handleAnnualButtonClick = useCallback(() => {
  //   if (!isMonthlyButtonActive) return;
  //   setIsMonthlyButtonActive(false);
  // }, [isMonthlyButtonActive]);

  useEffect(() => {
    getCurrentBillingPlan()
    .then((res) => {
      setCurrentPlan(res?.data || null);
      setIsFreeTrialExpired(!!res?.data?.isFreeTrialExpired);
    })
    .catch((err) => {
      console.log("onboarding getCurrentBillingPlan axios err=", err);
    });

    getAllBillingPlan()
      .then((res) => {
        const plans = Array.isArray(res?.data)
          ? res.data.filter((plan) => plan && plan.is_free !== true)
          : [];
        setBillingPlans(plans);
        if (status === PAID_PLANS_STATUS) {
          const onlyPaidPlans = plans.filter((obj) => !obj.is_free);
          setBillingPlans(onlyPaidPlans);
          //When status = 1, it indicates that the user has exceeded the revenue limit,
          //so we need to show a revenue exceeded limit message by updating the flag below.
          setIsExceedStatus(true);
          //Send Intercom event to track the user has seen the revenue limit exceeded page.
          trackAmplitudeUserShownRevenueLimitExceededPageEvent();
        }
        else if(status===PRO_PLAN_STATUS){
          setSinglePlanDisplay(true)
          const proBillingPlan = plans.filter((obj) =>
            billingPlanHasPurchaseOrderFeature(obj)
          );
          setBillingPlans(proBillingPlan);
        }
      })
      .catch((err) => {
        console.log("onboarding getAllBillingPlan axios err=", err);
        setBillingPlans([]);
      });

    getStoreDetails()
      .then((res) => {
        setIsStoreDetails(res.data[0]);
      })
      .catch((err) => {
        console.log("axios err=", err);
      });

    getAccountDetails()
      .then((res) => {
        //init intercom
        initIntercom(res.data, "");
      })
      .catch((err) => {
        //init intercom
        initIntercom("");
        console.log("axios err=", err);
      });

    return () => {
      console.log("axios cleanup.");
    };
  }, []);

  const displayedBillingPlans = getDisplayedBillingPlans(
    billingPlans,
    currentPlan,
    status
  );

  const openPolicy = (event, path) => {
    event.preventDefault();
    openEmbeddedAppPathInNewAdminTab(path);
  };

  return (
    <div id="billingPlanPageCustomLayoutIdentifier">
      <OnboardingShell
        step={location.pathname.startsWith(ONBOARDING) ? 2 : undefined}
        logoId="billingPlanPageLogo"
      >
        {isExceedStatus && isStoreDetails?.reason ? (
          <p className={classes.exceed}>{isStoreDetails.reason}</p>
        ) : null}

        {displayedBillingPlans.length === 0 ? (
          <p className={shell.subtitle}>Loading plans…</p>
        ) : null}

        {displayedBillingPlans.map((billingPlan, index) => {
          const monthlyPrice = !isMonthlyButtonActive
            ? billingPlan?.price?.annual
            : billingPlan?.price?.monthly;
          const priceSuffix = !isMonthlyButtonActive ? "/year" : "/month";
          const featureTitle = billingPlan?.featureTitle?.featureTitle;
          const isPurchaseOrderPlan =
            billingPlanHasPurchaseOrderFeature(billingPlan) && isSinglePlanDisplay;
          const isFreePlan = billingPlan?.is_free === true;
          const ctaClass =
            isFreePlan && isExceedStatus === true ? "startFreeBtn" : "startPaidBtn";

          return (
            <section
              key={billingPlan.id}
              className={index > 0 ? classes.extraPlan : undefined}
            >
              <h1 className={shell.title} id="billingPlansLabel">
                {billingPlan.plan_name}
              </h1>

              {isPurchaseOrderPlan ? (
                <p className={shell.subtitle}>
                  All the features of Forecasting Plan, plus create Shopify PO
                  using the Chrome extension.
                </p>
              ) : featureTitle ? (
                <p className={shell.subtitle} id="billingPlanSubtitle">
                  {featureTitle}
                </p>
              ) : !isFreePlan ? (
                <p className={shell.subtitle} id="billingPlanPageHeading">
                  Everything you need to stay ahead of stockouts
                </p>
              ) : null}

              <p className={classes.price} id="billingPlanPrice">
                ${monthlyPrice}
                <span className={classes.priceUnit}>{priceSuffix}</span>
              </p>

              <div className={`${ctaClass} ${classes.cta}`}>
                <Button
                  id={
                    isFreePlan && isExceedStatus === true
                      ? "startFree"
                      : "startPaid"
                  }
                  className={shell.primaryButton}
                  primary
                  fullWidth
                  textAlign="center"
                  loading={isSelectedButtonIndex === index}
                  disabled={isSubmitted}
                  onClick={() => onSelectBillingPlan(index, billingPlan)}
                >
                  {getPlanCtaLabel(
                    billingPlan,
                    currentPlan,
                    isFreeTrialExpired,
                    status
                  )}
                </Button>
              </div>

              <p className={classes.legal}>
                You'll confirm this on Shopify's billing screen. By continuing,
                you agree to the{" "}
                <a
                  href={PRIVACY}
                  className={shell.link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => openPolicy(event, PRIVACY)}
                >
                  developer's privacy policy
                </a>{" "}
                and{" "}
                <a
                  href={TERMS}
                  className={shell.link}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(event) => openPolicy(event, TERMS)}
                >
                  Terms and Conditions
                </a>
                .
              </p>

              {!isFreePlan && !isFreeTrialExpired ? (
                <p className={classes.trialNote} id="freeTrialText">
                  Pay nothing for 30 days & cancel anytime
                </p>
              ) : null}

              <BillingPlanFeatureList
                billingPlan={billingPlan}
                listId={`billing-plan-features-${billingPlan.id}`}
                variant="onboarding"
                hideSubtitle
              />
            </section>
          );
        })}
      </OnboardingShell>
      {props?.billingPlanError && (
        <CustomToast
          message={props.billingPlanError}
          onClose={() => props.setBillingPlanError("")}
        />
      )}
    </div>
  );
};

export default Billingplan;
